import { createSelector } from 'reselect';
import { ROOT_SLICE_AP_CLOSURE_REPORTS } from '../constants';
import { selectItems as selectReports, selectServerItems as selectServerReports } from '../main/selectors';
import { computeUserCompositeId } from '../main/transform';

export const selectSliceAPClosureReports = (state = {}) =>
	state[ROOT_SLICE_AP_CLOSURE_REPORTS] || state;

export const selectSelectedCustomers = state => {
  const slice = selectSliceAPClosureReports(state);
	const { selectedCustomers } = slice;
	return selectedCustomers;
};

export const selectSelectedOwners = state => {
  const slice = selectSliceAPClosureReports(state);
	const { selectedOwners } = slice;
	return selectedOwners;
};

export const selectSelectedParentTypeKeys = state => {
  const slice = selectSliceAPClosureReports(state);
	const { selectedParentTypeKeys } = slice;
	return selectedParentTypeKeys;
};

export const selectSelectedParentNames = state => {
  const slice = selectSliceAPClosureReports(state);
	const { selectedParentNames } = slice;
	return selectedParentNames;
};

export const selectSelectedSurveyTypes = state => {
  const slice = selectSliceAPClosureReports(state);
	const { selectedSurveyTypes } = slice;
	return selectedSurveyTypes;
};

// ***********
// LISTS - CUSTOMERS
// ***********

export const selectCustomers = createSelector(
	selectReports, 
	(reports) => {
		const customers = new Set([]);
		reports.forEach(r => {
			if (r?.customer?.name) {
				customers.add(r.customer.name);
			}
		});
		return [...customers].sort();
	}
);

export const selectSelectedCustomersList = createSelector(
	selectCustomers,
	selectSelectedCustomers,
	(customers, selectedCustomers) => {
		return customers.filter(c => selectedCustomers[c]);
	}
)

// ***********
// LISTS - OWNERS
// ***********

export const selectOwners = createSelector(
	selectReports, 
	(reports) => {
		const ownersMap = reports.reduce((acc, r) => {
			if (r?.owner?.compositeId && !acc[r?.compositeId]) {
				acc[r.owner.compositeId] = r.owner;
			}
			return acc;
		}, {});
		const owners = Object.keys(ownersMap).map(compositeId => ownersMap[compositeId])
		return owners;
	}
);

export const selectSelectedOwnersList = createSelector(
	selectOwners,
	selectSelectedOwners,
	(owners, selectedOwners) => {
		return owners.filter(a => a?.compositeId && selectedOwners[a?.compositeId]);
	}
)

// ***********
// LISTS - PARENT TYPE KEYS
// ***********

export const selectParentTypeKeys = createSelector(
	selectReports, 
	(reports) => {
		const parentTypeKeys = new Set([]);
		reports.forEach(r => {
			if (r?.parent?.typeKey) {
				parentTypeKeys.add(r.parent.typeKey);
			}
		});
		return [...parentTypeKeys].sort();
	}
);

export const selectSelectedParentTypeKeysList = createSelector(
	selectParentTypeKeys,
	selectSelectedParentTypeKeys,
	(parentTypeKeys, selectedParentTypeKeys) => {
		return parentTypeKeys.filter(key => selectedParentTypeKeys[key]);
	}
)

// ***********
// LISTS - PARENT NAMES
// ***********

export const selectParentNames = createSelector(
	selectReports, 
	(reports) => {
		const parentNames = new Set([]);
		reports.forEach(r => {
			if (r?.parent?.typeKey) {
				parentNames.add(r.parent.name);
			}
		});
		return [...parentNames].sort();
	}
);

export const selectSelectedParentNamesList = createSelector(
	selectParentNames,
	selectSelectedParentNames,
	(parentNames, selectedParentNames) => {
		return parentNames.filter(name => selectedParentNames[name]);
	}
)

// ***********
// LISTS - SURVEY TYPES
// ***********

export const selectSurveyTypes = createSelector(
	selectReports, 
	(reports) => {
		const surveyTypes = new Set([]);
		reports.forEach(r => {
			if (r?.survey?.type) {
				surveyTypes.add(r.survey.type);
			}
		});
		return [...surveyTypes].sort();
	}
);

export const selectSelectedSurveyTypesList = createSelector(
	selectSurveyTypes,
	selectSelectedSurveyTypes,
	(surveyTypes, selectedSurveyTypes) => {
		return surveyTypes.filter(type => selectedSurveyTypes[type]);
	}
)


// ***********
// LISTS - REPORTS
// ***********

export const selectFilteredItemsList = createSelector(
	selectReports,
	selectSelectedCustomers,
	selectSelectedOwners,
	selectSelectedParentTypeKeys,
	selectSelectedParentNames,
	selectSelectedSurveyTypes,
	(reports, customersMap, ownersMap, parentTypeKeysMap, parentNamesMap, surveyTypesMap) => {
		const hasSelectedCustomers = customersMap && Object.keys(customersMap).length > 0;
		const hasSelectedOwners = ownersMap && Object.keys(ownersMap).length > 0;
		const hasSelectedParentTypeKeys = parentTypeKeysMap && Object.keys(parentTypeKeysMap).length > 0;
		const hasSelectedParentNames = parentNamesMap && Object.keys(parentNamesMap).length > 0;
		const hasSelectedSurveyTypes = surveyTypesMap && Object.keys(surveyTypesMap).length > 0;

		if (!hasSelectedCustomers && !hasSelectedOwners && !hasSelectedParentTypeKeys && !hasSelectedParentNames && !hasSelectedSurveyTypes) {
			return reports;
		}

		const hasCustomer = (report) => {
			if (!hasSelectedCustomers) {
				return true;
			}
			return customersMap[report?.customer?.name];
		};

		const hasOwner = (report) => {
			if (!hasSelectedOwners) {
				return true;
			}
			return ownersMap[report?.owner?.compositeId];
		};

		const hasParentTypeKey = (report) => {
			if (!hasSelectedParentTypeKeys) {
				return true;
			}
			return parentTypeKeysMap[report?.parent?.typeKey];
		};

		const hasParentName = (report) => {
			if (!hasSelectedParentNames) {
				return true;
			}
			return parentNamesMap[report?.parent?.name];
		};

		const hasSurveyType = (report) => {
			if (!hasSelectedSurveyTypes) {
				return true;
			}
			return surveyTypesMap[report?.survey?.type];
		};

		return reports.filter(report => {
			return hasCustomer(report) && hasOwner(report) && hasParentTypeKey(report) && hasParentName(report) && hasSurveyType(report);
		});
	}
);

export const selectFilteredServerItemsList = createSelector(
	selectServerReports,
	selectSelectedCustomers,
	selectSelectedOwners,
	selectSelectedParentTypeKeys,
	selectSelectedParentNames,
	selectSelectedSurveyTypes,
	(reports, customersMap, ownersMap, parentTypeKeysMap, parentNamesMap, surveyTypesMap) => {
		const hasSelectedCustomers = customersMap && Object.keys(customersMap).length > 0;
		const hasSelectedOwners = ownersMap && Object.keys(ownersMap).length > 0;
		const hasSelectedParentTypeKeys = parentTypeKeysMap && Object.keys(parentTypeKeysMap).length > 0;
		const hasSelectedParentNames = parentNamesMap && Object.keys(parentNamesMap).length > 0;
		const hasSelectedSurveyTypes = surveyTypesMap && Object.keys(surveyTypesMap).length > 0;

		if (!hasSelectedCustomers && !hasSelectedOwners && !hasSelectedParentTypeKeys && !hasSelectedParentNames && !hasSelectedSurveyTypes) {
			return reports;
		}

		const hasCustomer = (report) => {
			if (!hasSelectedCustomers) {
				return true;
			}
			return customersMap[report?.customer_name];
		};

		const hasOwner = (report) => {
			if (!hasSelectedOwners) {
				return true;
			}
			return ownersMap[computeUserCompositeId(report?.userid, report?.user_name)];
		};

		const hasParentTypeKey = (report) => {
			if (!hasSelectedParentTypeKeys) {
				return true;
			}
			return parentTypeKeysMap[report?.parent_type_key];
		};

		const hasParentName = (report) => {
			if (!hasSelectedParentNames) {
				return true;
			}
			return parentNamesMap[report?.parent_name];
		};

		const hasSurveyType = (report) => {
			if (!hasSelectedSurveyTypes) {
				return true;
			}
			return surveyTypesMap[report?.survey_type];
		};

		return reports.filter(report => {
			return hasCustomer(report) && hasOwner(report) && hasParentTypeKey(report) && hasParentName(report) && hasSurveyType(report);
		});
	}
);

